import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase } from '../utils/authHeaders';
import {
  capabilitiesAllow, errorMessage, requestConfig, scopeKey, useMktScope, readMktScope,
} from './MarketplaceShared';
import './Workforce.css';

export const forecastApi = axios.create({ baseURL: `${apiBase()}/api/demand-forecast` });

export function useForecastNavEnabled(token, probe = true) {
  const scope = useMktScope(token);
  const key = scopeKey(scope);
  const [allowedKey, setAllowedKey] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setAllowedKey('');
    if (probe && scope.orgId && scope.token) {
      forecastApi.get('/capabilities', requestConfig(scope, controller.signal))
        .then(res => { if (!controller.signal.aborted && capabilitiesAllow(res?.data)) setAllowedKey(key); })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [key, scope, probe]);
  return allowedKey === key;
}

function useForecastRuntime(scope) {
  const lifetime = useRef(new AbortController());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    return () => controller.abort();
  }, []);
  const active = useCallback(
    () => !lifetime.current.signal.aborted && scopeKey(scope) === scopeKey(readMktScope()),
    [scope],
  );
  const request = useCallback(async (method, path, { body, params } = {}) => {
    const config = { ...requestConfig(scope, lifetime.current.signal) };
    if (params) config.params = params;
    const res = ['get', 'delete'].includes(method)
      ? await forecastApi[method](path, config)
      : await forecastApi[method](path, body, config);
    return res.data;
  }, [scope]);
  return { active, request, busy, setBusy, error, setError, notice, setNotice };
}

export default function DemandForecast() {
  const scope = useMktScope();
  return <DemandForecastWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function DemandForecastWorkspace({ scope }) {
  const { active, request, busy, setBusy, error, setError, notice, setNotice } = useForecastRuntime(scope);
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState('');
  const [latest, setLatest] = useState(null);
  const canOperate = Boolean(caps?.can_operate);
  const hasSite = Boolean(scope.locationId);

  const loadCaps = useCallback(async () => {
    setLoading(true); setBlocked(''); setError('');
    try {
      if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to use Forecast.');
      const c = await request('get', '/capabilities');
      if (!active()) return;
      if (!capabilitiesAllow(c)) {
        setCaps(null);
        setBlocked('Forecast is not enabled for this organization, or you do not have access.');
        return;
      }
      setCaps(c);
    } catch (e) {
      if (active()) { setCaps(null); setBlocked(errorMessage(e)); }
    } finally {
      if (active()) setLoading(false);
    }
  }, [scope, request, active, setError]);
  useEffect(() => { loadCaps(); }, [loadCaps]);

  const loadLatest = useCallback(async () => {
    if (!hasSite) { setLatest(null); return; }
    try {
      const data = await request('get', '/latest', { params: { location_id: scope.locationId } });
      if (active()) setLatest(data);
    } catch (e) {
      if (active()) {
        if (e?.response?.status === 404) setLatest(null);
        else setError(errorMessage(e));
      }
    }
  }, [request, active, hasSite, scope.locationId, setError]);

  useEffect(() => {
    if (caps) loadLatest();
  }, [caps, loadLatest]);

  const compute = async () => {
    if (!hasSite || !canOperate) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const asOf = new Date().toISOString().replace(/\.\d{3}Z$/, '+00:00');
      const data = await request('post', '/compute-auto', {
        body: {
          location_id: scope.locationId,
          as_of: asOf,
          horizon_weeks: 13,
          recent_weeks: 8,
          seasonality: {},
        },
      });
      if (active()) {
        setLatest(data);
        setNotice('Forecast computed.');
      }
    } catch (e) {
      if (active()) setError(errorMessage(e));
    } finally {
      if (active()) setBusy(false);
    }
  };

  if (loading) return <div className="wf-ops" data-testid="forecast-loading">Loading forecast…</div>;
  if (blocked || !caps) {
    return (
      <div className="wf-ops wf-blocked" data-testid="forecast-blocked">
        <p>{blocked || 'Forecast is not enabled for this organization, or you do not have access.'}</p>
      </div>
    );
  }

  const lines = Array.isArray(latest?.lines) ? latest.lines : [];

  return (
    <div className="wf-ops" data-testid="demand-forecast-page">
      <header>
        <div>
          <h1>Demand forecast</h1>
          <p className="wf-muted">Weekly ingredient demand from sales history and requisitions.</p>
        </div>
        <div className="wf-actions">
          <button type="button" onClick={loadLatest} disabled={busy || !hasSite}>Refresh</button>
          {canOperate && (
            <button
              type="button"
              className="wf-primary"
              aria-label="Compute forecast"
              onClick={compute}
              disabled={busy || !hasSite}
            >
              Compute
            </button>
          )}
        </div>
      </header>
      {error && <div className="wf-error" role="alert">{error}</div>}
      {notice && <div className="wf-notice" role="status">{notice}</div>}
      {latest?.input_snapshot && <p className="wf-muted">Source: completed POS and DirectConnect orders, complete UTC weeks; recipe AP quantities. {latest.input_snapshot.source_order_ids?.length || 0} source orders.</p>}
      {(latest?.input_snapshot?.warnings || []).map(warning => <p role="status" key={warning}>{warning}</p>)}
      {!hasSite && <p className="wf-empty">Select a site to compute or view the latest forecast.</p>}
      {hasSite && lines.length === 0 && (
        <p className="wf-empty" data-testid="forecast-empty-lines">No forecast lines yet. Run Compute to generate.</p>
      )}
      {lines.length > 0 && (
        <div className="wf-table-wrap">
          <table data-testid="forecast-lines-table">
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>Week start</th>
                <th>Qty</th>
                <th>UOM</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={`${line.ingredient_id}-${line.week_start}-${idx}`}>
                  <td>{line.ingredient_id}</td>
                  <td>{line.week_start}</td>
                  <td>{line.qty}</td>
                  <td>{line.uom || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
